import { makeVar } from '@apollo/client/cache/inmemory/reactiveVars'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { Comment, TrackedChange } from 'models/changes'
import { ProjectElementMetadata } from 'models/projectElements'
import { resetOnFocusProperty } from './property'

interface WorkspaceElement {
    selectedElementNode: ProjectElementNode | null
    projectMetadata: Partial<ProjectElementMetadata> | undefined
    trackedChanges?: TrackedChange[] | null
    elementNotesCount: number
    isLevelOfSpecificationAutomation: boolean
    familyProjectMetadata?: Partial<ProjectElementMetadata> | undefined
}

const initialWorkspaceElement = {
    selectedElementNode: null,
    projectMetadata: undefined,
    trackedChanges: null,
    elementNotesCount: 0,
    isLevelOfSpecificationAutomation: false,
    familyProjectMetadata: undefined,
}
export enum ElementType {
    SUSTAINABILITY = 'SUSTAINABILITY',
    PRODUCTTYPE = 'PRODUCTTYPE',
    DEFAULT = 'DEFAULT',
    PRODUCTFAMILY = 'PRODUCTFAMILY',
}
export const workspaceSelectedElement = makeVar<WorkspaceElement>({
    ...initialWorkspaceElement,
})

export const setWorkspaceSelectedElement = (
    selectedProjectElement: ProjectElementNode | null
) => {
    const currentWorkspaceElement = workspaceSelectedElement()
    workspaceSelectedElement({
        ...currentWorkspaceElement,
        selectedElementNode: selectedProjectElement,
    })

    resetOnFocusProperty()
}

export const setProjectElementMetadata = (
    projectMetadata: Partial<ProjectElementMetadata> | undefined
) => {
    const currentWorkspaceElement = workspaceSelectedElement()
    workspaceSelectedElement({
        ...currentWorkspaceElement,
        projectMetadata,
    })
}

/**
 * Set Family Project Element Metadata to be used for Level of Specification Automation
 *
 * @param { Partial<ProjectElementMetadata>} familyProjectMetadata - The updated project metadata of selected family element.
 */
export const setFamilyProjectElementMetadata = (
    familyProjectMetadata: Partial<ProjectElementMetadata> | undefined
) => {
    const currentWorkspaceElement = workspaceSelectedElement()
    workspaceSelectedElement({
        ...currentWorkspaceElement,
        familyProjectMetadata,
    })
}

export const setElementTrackedChanges = (trackedChanges: TrackedChange[]) => {
    const currentWorkspaceElement = workspaceSelectedElement()
    workspaceSelectedElement({
        ...currentWorkspaceElement,
        trackedChanges,
    })
}

export const addTrackChangeComment = (comment: Comment) => {
    const currentWorkspaceElement = workspaceSelectedElement()
    workspaceSelectedElement({
        ...currentWorkspaceElement,
        trackedChanges: currentWorkspaceElement.trackedChanges?.map(
            (change) => {
                if (change.id === comment.trackChangeId) {
                    return {
                        ...change,
                        trackChangeComments: change.trackChangeComments.concat(
                            comment
                        ),
                    }
                } else {
                    return change
                }
            }
        ),
    })
}

export const setTrackChangeComment = (
    trackChangeId: string,
    comments: Comment[]
) => {
    const currentWorkspaceElement = workspaceSelectedElement()
    workspaceSelectedElement({
        ...currentWorkspaceElement,
        trackedChanges: currentWorkspaceElement.trackedChanges?.map(
            (change) => {
                if (change.id === trackChangeId) {
                    return {
                        ...change,
                        trackChangeComments: comments,
                    }
                } else {
                    return change
                }
            }
        ),
    })
}

export const updateTrackChangeComment = (comment: Comment) => {
    const currentWorkspaceElement = workspaceSelectedElement()
    workspaceSelectedElement({
        ...currentWorkspaceElement,
        trackedChanges: currentWorkspaceElement.trackedChanges?.map(
            (change) => {
                if (change.id === comment.trackChangeId) {
                    return {
                        ...change,
                        trackChangeComments: change.trackChangeComments.map(
                            (oldComment) => {
                                if (oldComment.id === comment.id) {
                                    return {
                                        ...oldComment,
                                        updatedDate: comment.updatedDate,
                                        comment: comment.comment,
                                    }
                                } else {
                                    return oldComment
                                }
                            }
                        ),
                    }
                } else {
                    return change
                }
            }
        ),
    })
}

export const setElementNotesCount = (count: number) => {
    const currentWorkspaceElement = workspaceSelectedElement()
    workspaceSelectedElement({
        ...currentWorkspaceElement,
        elementNotesCount: count,
    })
}

export const updateElementNotesCount = (action: 'add' | 'subtract') => {
    const currentWorkspaceElement = workspaceSelectedElement()
    workspaceSelectedElement({
        ...currentWorkspaceElement,
        elementNotesCount:
            action === 'add'
                ? currentWorkspaceElement.elementNotesCount + 1
                : currentWorkspaceElement.elementNotesCount - 1,
    })
}

export const setIsLevelOfSpecificationAutomation = (val: boolean) => {
    const currentWorkspaceElement = workspaceSelectedElement()
    workspaceSelectedElement({
        ...currentWorkspaceElement,
        isLevelOfSpecificationAutomation: val,
    })
}

export enum DTVName {
    SUSTAINABILITY = 'Sustainablity',
    SECTION = 'Section',
}
